import { Outlet, Link, useLocation } from "react-router-dom";
import { Home, Users, AlertCircle, LogOut, Menu, X, DollarSign, LogIn, Activity, MessageSquare, Target, Image, BookOpen, ClipboardList, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import NotificationBell from "@/components/NotificationBell";

export default function ParentLayout({ user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Dynamic page title based on route
  useEffect(() => {
    const titles = {
      "/": "My Children",
      "/activity": "Activity Feed",
      "/work-samples": "Work Samples",
      "/lesson-plans": "Lesson Plans",
      "/homework": "Homework",
      "/observations": "Observations",
      "/milestones": "Milestones",
      "/messages": "Messages",
      "/checkin": "Check In/Out",
      "/billing": "Billing",
      "/reports": "Reports",
    };
    const title = titles[location.pathname] || "Parent Portal";
    document.title = `${title} | ConnectEd`;
  }, [location.pathname]);

  const navigation = [
    { name: "My Children", href: "/", icon: Home },
    { name: "Activity Feed", href: "/activity", icon: Activity },
    { name: "Work Samples", href: "/work-samples", icon: Image },
    { name: "Lesson Plans", href: "/lesson-plans", icon: BookOpen },
    { name: "Homework", href: "/homework", icon: ClipboardList },
    { name: "Observations", href: "/observations", icon: Eye },
    { name: "Milestones", href: "/milestones", icon: Target },
    { name: "Messages", href: "/messages", icon: MessageSquare },
    { name: "Check In/Out", href: "/checkin", icon: LogIn },
    { name: "Billing", href: "/billing", icon: DollarSign },
    { name: "Compliance Reports", href: "/reports", icon: AlertCircle },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAF8]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-100 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/connected-logo.png" alt="ConnectEd" className="h-12" />
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell location="mobile" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 shadow-sm z-30 transition-transform duration-300`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-slate-100">
            <img src="/connected-logo.png" alt="ConnectEd" className="h-14" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group ${
                    isActive
                      ? "bg-sage-50 text-sage-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-sage-600"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-sage-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-sage-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{user.name}</p>
                <p className="text-xs text-slate-500">Parent</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        {/* Desktop notification bar */}
        <div className="hidden lg:flex sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 items-center justify-between">
          <h1 className="text-xl font-heading font-bold text-slate-800">Welcome back, {user.name?.split(' ')[0]}!</h1>
          <NotificationBell location="desktop" />
        </div>
        <div className="p-6 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
